import { FC, useMemo, useState } from 'react'

import { Button, ButtonSizes, ButtonVariants } from '@inno-ui/button'
import {
  CodeIcon,
  ExclamationTriangleLineIcon,
  StarsLineIcon
} from '@inno-ui/icons'
import { toast } from '@inno-ui/toast'
import { Tooltip, TooltipPlacements } from '@inno-ui/tooltip'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  GET_ARTIFACTS_QUERY_KEY,
  REVIEW_QUEUE_QUERY_KEY,
  useAiReviewArtifactMutation,
  useReviewArtifactMutation
} from 'shared/api/forgeApi'

import { AiReviewResult } from '../../types'
import { ReviewArtifactCardProps as ReviewCardProps } from './ReviewArtifactCard.types'
import styles from './ReviewQueue.module.scss'
import { CardShell } from './components/CardShell'
import { CodeModal } from './components/CodeModal'
import { DecisionForm } from './components/DecisionForm'
import { HostsEditor } from './components/HostsEditor'
import { RevealModal } from './components/RevealModal'
import { reviewFeedback } from './components/reviewFeedback'

// Pending widget reviewer surface — approve/decline decision with host
// allowlist editing and an on-demand AI security pass.
export const ReviewCard: FC<ReviewCardProps> = ({ artifact }) => {
  const { t } = useTranslation('translation', { keyPrefix: 'widgets.aiAgent' })
  const queryClient = useQueryClient()
  const publicId = artifact.public_id
  const [hosts, setHosts] = useState<string[]>([])
  const [codeOpen, setCodeOpen] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [aiOverride, setAiOverride] = useState<AiReviewResult | null>(null)
  const ai = useMemo<AiReviewResult | null>(
    () =>
      aiOverride ??
      (artifact.ai_review_verdict
        ? {
            verdict: artifact.ai_review_verdict,
            findings: artifact.ai_review_findings ?? []
          }
        : null),
    [aiOverride, artifact.ai_review_verdict, artifact.ai_review_findings]
  )

  const reviewMutation = useReviewArtifactMutation({
    onSuccess: (_data, variables) => {
      setHosts([])
      queryClient.invalidateQueries({ queryKey: [REVIEW_QUEUE_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [GET_ARTIFACTS_QUERY_KEY] })
      toast.success(reviewFeedback(variables.decision, 'success', t))
    },
    onError: (_err, variables) => {
      toast.error(reviewFeedback(variables.decision, 'failed', t))
    }
  })
  const aiMutation = useAiReviewArtifactMutation({
    onSuccess: result => {
      setAiOverride(result)
      toast.success(reviewFeedback('ai_review', 'success', t))
    },
    onError: () => {
      toast.error(reviewFeedback('ai_review', 'failed', t))
    }
  })

  const submit = (decision: 'approve' | 'decline', reason?: string) =>
    reviewMutation.mutate({
      publicId,
      decision,
      allowedHosts: decision === 'approve' ? hosts : undefined,
      reason
    })

  return (
    <CardShell
      artifact={artifact}
      verdict={ai}
      actions={
        <>
          {/* reveal runs the widget on the reviewer's account; tooltip warns
              before they click */}
          <Tooltip
            overlay={t('preview_hidden_hint')}
            placement={TooltipPlacements.Bottom}
          >
            <Button
              variant={ButtonVariants.Light}
              size={ButtonSizes.S}
              icon={ExclamationTriangleLineIcon}
              label={t('reveal_preview')}
              onClick={() => setPreviewOpen(true)}
            />
          </Tooltip>
          <Button
            variant={ButtonVariants.Light}
            size={ButtonSizes.S}
            icon={StarsLineIcon}
            label={t('let_ai_review')}
            isLoading={aiMutation.isPending}
            disabled={aiMutation.isPending}
            onClick={() => aiMutation.mutate(publicId)}
          />
          <Button
            variant={ButtonVariants.Light}
            size={ButtonSizes.S}
            icon={CodeIcon}
            label={t('view_code')}
            onClick={() => setCodeOpen(true)}
          />
        </>
      }
    >
      <div className={styles.lower}>
        <HostsEditor artifact={artifact} onChange={setHosts} />
        <DecisionForm
          variant='approve-decline'
          busy={reviewMutation.isPending}
          onSubmit={submit}
        />
      </div>
      <RevealModal
        publicId={publicId}
        title={artifact.title}
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
      />
      <CodeModal
        publicId={publicId}
        title={artifact.title}
        isOpen={codeOpen}
        onClose={() => setCodeOpen(false)}
      />
    </CardShell>
  )
}
